import React, { useState, useEffect } from "react";
import { SingleCommentProps } from "./interfaces";
import { useUser } from "@services/UserContext";

const SingleComment: React.FC<SingleCommentProps> = ({
  comment,
  handleReply,
  handleCommentDelete,
  parentComment = false,
  setReplyInputVisible,
}) => {
  const [editing, setEditing] = useState(false);
  const [commentText, setCommentText] = useState(comment.message);
  const { user } = useUser();
  const [isOpen, setIsOpen] = useState(false);

  const canEdit = user?.email === comment.author.email;

  const created_at = new Date(comment.created_at);

  const getRelativeTime = (timestamp: string | number | Date): string => {
    const now = new Date();
    const createdAt = new Date(timestamp);
    const diffInSeconds = Math.floor(
      (now.getTime() - createdAt.getTime()) / 1000
    );

    if (diffInSeconds < 60) {
      return "Just now";
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return diffInMinutes === 1 ? "1 min ago" : `${diffInMinutes} mins ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return diffInHours === 1 ? "1 hour ago" : `${diffInHours} hours ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      return diffInDays === 1 ? "1 day ago" : `${diffInDays} days ago`;
    }

    const diffInWeeks = Math.floor(diffInDays / 7);
    return diffInWeeks === 1 ? "1 week ago" : `${diffInWeeks} weeks ago`;
  };

  const CommentTime = ({
    createdAt,
  }: {
    createdAt: string | number | Date;
  }) => {
    const [timeAgo, setTimeAgo] = useState(getRelativeTime(createdAt));

    useEffect(() => {
      const interval = setInterval(() => {
        setTimeAgo(getRelativeTime(createdAt));
      }, 60000); // Updates every 1 minute

      return () => clearInterval(interval); // Cleanup on unmount
    }, [createdAt]);

    return <span>{timeAgo}</span>;
  };

  return (
    <div className="comment flex gap-2 my-3 items-center">
      {/* Avatar */}
      <div className="comment bg-red-500 text-white w-8 h-8 flex items-center justify-center rounded-full font-bold">
        {comment.author.first_name[0]}
      </div>

      <div className="comment relative flex-1">
        {/* Actions (Edit & Delete) - Show on Hover */}

        {/* Comment Content */}
        <div className="comment flex-1">
          <strong className="comment text-gray-900">
            {comment.author.first_name} {comment.author.last_name}
          </strong>
          <span className="comment text-gray-500 text-xs ml-2">
            <CommentTime createdAt={created_at} />
          </span>
        </div>

        {/* Edit Mode */}
        {editing ? (
          <div className="comment items-center gap-2 p-2 border border-gray-300 shadow-md rounded-lg bg-white">
            <div className="flex-1 flex">
              <input
                type="text"
                className="comment border-none outline-none bg-gray-100 px-2 py-1 rounded-md flex-1 mr-3"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
              />
              <button
                className="comment bg-blue-500 text-white px-3 py-1 rounded-lg cursor-pointer"
                onClick={() => {
                  if (commentText.trim() !== "") {
                    setEditing(false);
                    handleReply(commentText, `${comment.id}`, true);
                  }
                }}
              >
                Save
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p className="comment mt-0 text-gray-700">{comment.message}</p>
            <div className="absolute top-0 right-0">
              <div className="relative">
                <div className="flex">
                  {parentComment && (
                    <button
                      onClick={() => handleCommentDelete(`${comment.id}`, true)}
                      className="p-1 text-gray-500 hover:text-gray-700"
                    >
                      <img src="/check_circle.svg" className="w-5 h-5" alt="" />
                    </button>
                  )}
                  <button
                    onClick={() => setIsOpen((prev) => !prev)}
                    className="p-1 text-gray-500 hover:text-gray-700"
                  >
                    ⋮
                  </button>
                </div>

                {isOpen && (
                  <div className="absolute top-8 right-0 z-10 bg-black shadow-md rounded-lg py-1 px-4">
                    <div className="py-2">
                      {
                        <>
                          {canEdit && comment.can_edit && (
                            <button
                              onClick={() => setEditing((prev) => !prev)}
                              className="text-xs text-white mr-4"
                            >
                              Edit...
                            </button>
                          )}
                          {!parentComment && comment.can_delete && (
                            <button
                              onClick={() =>
                                handleCommentDelete(`${comment.id}`, false)
                              }
                              className="text-xs text-white"
                            >
                              {" "}
                              Remove
                            </button>
                          )}
                        </>
                      }
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SingleComment;
